import { useCallback, useEffect, useMemo, useRef } from "react";
import { EVENTS, STATUS, useJoyride } from "react-joyride";
import { TOUR_IDS } from "@demandproai/shared";
import { useDemandStudioContext } from "../contexts/DemandStudioContext";
import { useTourContext } from "../contexts/TourContext";
import { TOUR_OPTIONS } from "../constants/tour";
import { buildDemandStudioTourSteps } from "../tours/demandStudioTourSteps";
import TourTooltip from "../components/tour/TourTooltip";
import useIsModalOpen from "./useIsModalOpen";
import useTourSpotlightSync from "./useTourSpotlightSync";
import useTourAnchorRecovery from "./useTourAnchorRecovery";

const useDemandStudioTour = () => {
   const { isInitialLoadPending, showPreview, isTabView, toggleViewMode, handleTogglePreview } =
      useDemandStudioContext();
   const { registerTour, requestedTourId, clearTourRequest, isTourPending, completeTour, dismissTour } =
      useTourContext();
   const isModalOpen = useIsModalOpen();

   // Read through refs so the step hooks stay stable and the steps array is not
   // rebuilt underneath a running tour
   const showPreviewRef = useRef(showPreview);
   const isTabViewRef = useRef(isTabView);

   useEffect(() => {
      showPreviewRef.current = showPreview;
   }, [showPreview]);

   useEffect(() => {
      isTabViewRef.current = isTabView;
   }, [isTabView]);

   // Puts the studio in the view a stop's target lives in, and nothing more.
   // Both of these are toggles with no setter that takes a target, so the
   // current value has to be read and only disagreement acted on.
   //
   // A stop that says nothing is left alone deliberately. Closing a preview the
   // user opened costs them a full regeneration to get it back, so only the
   // stops anchored inside the editor ask for it.
   //
   // handleTogglePreview is the one that only moves the view. The Preview button
   // itself runs handlePreview, which flushes edits and regenerates the whole
   // demand, and a tour must not set that off on the user's behalf.
   const prepareSurface = useCallback(
      async ({ needsEditorSurface, needsTabView }) => {
         // the refs are written back as well as read, because the effects that
         // sync them do not run until the next render and two hooks can settle
         // inside one, which would toggle twice and undo the first
         if (needsEditorSurface && showPreviewRef.current) {
            showPreviewRef.current = false;
            handleTogglePreview();
         }

         if (needsTabView !== undefined && needsTabView !== isTabViewRef.current) {
            isTabViewRef.current = needsTabView;
            toggleViewMode();
         }
      },
      [handleTogglePreview, toggleViewMode]
   );

   const steps = useMemo(
      () => buildDemandStudioTourSteps({ prepareSurface }),
      [prepareSurface]
   );

   // The body is a skeleton until the studio has hydrated, so none of the
   // anchors exist yet
   const shouldAutoRun = !isInitialLoadPending && isTourPending(TOUR_IDS.DEMAND_STUDIO);

   const handleTourEvent = useCallback(
      (data) => {
         if (data.type !== EVENTS.TOUR_END) {
            return;
         }

         if (data.status === STATUS.SKIPPED) {
            dismissTour(TOUR_IDS.DEMAND_STUDIO);
         } else {
            completeTour(TOUR_IDS.DEMAND_STUDIO);
         }

         clearTourRequest();
      },
      [completeTour, dismissTour, clearTourRequest]
   );

   const { Tour, controls, state, step } = useJoyride({
      steps,
      run: shouldAutoRun,
      continuous: true,
      tooltipComponent: TourTooltip,
      options: TOUR_OPTIONS,
      onEvent: handleTourEvent,
   });

   useTourSpotlightSync({ step, state });
   useTourAnchorRecovery({ step, state, controls });

   // Resuming is only correct for a tour that is mid run, so this is read rather
   // than depended on: a status change must not itself trigger a pause
   const isMidRunRef = useRef(false);
   isMidRunRef.current = [STATUS.RUNNING, STATUS.PAUSED].includes(state.status);

   // The studio has no modal of its own, but the shell's help modal renders
   // below the tour overlay and is where the replay action lives
   useEffect(() => {
      if (!isMidRunRef.current) {
         return;
      }

      if (isModalOpen) {
         controls.stop();
      } else {
         controls.start();
      }
   }, [isModalOpen, controls]);

   // A replay rewinds to the first stop rather than resuming where the user left off
   useEffect(() => {
      if (requestedTourId === TOUR_IDS.DEMAND_STUDIO) {
         controls.reset(true);
      }
   }, [requestedTourId, controls]);

   useEffect(() => registerTour(TOUR_IDS.DEMAND_STUDIO), [registerTour]);

   return { Tour };
};

export default useDemandStudioTour;
